import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { 
  User,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../firebase';
import {
  AccountStatus,
  accountStatusFromDoc,
} from '../features/auth/accountAccess';

interface AuthContextType {
  user: User | null;
  /**
   * True until the auth state, the administrator claim AND the approval status
   * have all resolved. Deliberately covers all three: consumers that gate on
   * `loading` then cannot render before any of the checks is known, which is
   * what prevents both a flash of administration content and — since the
   * status joined it — a flash of the dashboard to an account that has just
   * signed up and is not approved.
   */
  loading: boolean;
  /**
   * Whether the signed-in user's ID token carries the `admin` custom claim.
   *
   * Visibility only — never a security boundary. The server re-checks every
   * administration operation via `isGlobalAdmin`, which is unchanged.
   *
   * Read from the CACHED token. A claim granted after the current token was
   * issued will not appear until the token refreshes (Firebase refreshes
   * roughly hourly) or the account signs out and back in. We deliberately do
   * not force a refresh here: that would add a network round trip to every
   * page load for every customer in order to serve the rare case of an
   * account whose admin grant is minutes old.
   */
  isAdmin: boolean;
  isSuperAdmin: boolean;
  /**
   * The signed-in account's approval status, read LIVE from users/{uid}.
   *
   * A live subscription rather than a one-shot read so that an approval takes
   * effect in the tab the applicant already has open — they are, by definition,
   * sitting on a screen waiting for exactly this to change. It also matches how
   * the rest of the app reads Firestore.
   *
   * `unreadable` is the deliberate FAIL-OPEN value: if the read errors, the
   * account is treated as approved rather than locked out. See
   * `features/auth/accountAccess`. Meaningless while `loading` is true.
   */
  accountStatus: AccountStatus;
  /** True for an administrator-provisioned account until first-use rotation. */
  mustChangePassword: boolean;
  mfaRequired: boolean;
  mfaEnrolled: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string) => Promise<void>;
  /** Returns false when the user cancels the confirmation. */
  logout: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [accountStatus, setAccountStatus] = useState<AccountStatus>('absent');
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaEnrolled, setMfaEnrolled] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // `cancelled` guards against a fast sign-out landing after a slower token
    // read resolves, which would otherwise leave isAdmin true with no session.
    let cancelled = false;
    // Torn down on every auth change, so a sign-out or an account switch can
    // never leave the previous account's status subscription running and
    // writing into this state.
    let stopStatus: (() => void) | null = null;

    const unsubscribe = onAuthStateChanged(auth, async (nextUser) => {
      if (cancelled) return;
      stopStatus?.();
      stopStatus = null;
      // Fail closed while a new account's claims resolve. Without this reset,
      // switching directly from a super admin could briefly expose its UI to
      // the next signed-in account using the previous React state.
      setLoading(true);
      setIsAdmin(false);
      setIsSuperAdmin(false);
      setUser(nextUser);

      if (!nextUser) {
        setIsAdmin(false);
        setIsSuperAdmin(false);
        setAccountStatus('absent');
        setMustChangePassword(false);
        setMfaRequired(false);
        setMfaEnrolled(false);
        setLoading(false);
        return;
      }

      // Resolve the claim BEFORE clearing `loading`, so no consumer can render
      // administration content in the window between sign-in and the check.
      try {
        const token = await nextUser.getIdTokenResult();
        if (cancelled) return;
        setIsAdmin(token.claims.admin === true);
        setIsSuperAdmin(token.claims.superAdmin === true);
      } catch {
        // A token read failure must fail CLOSED — treat as not an administrator.
        if (cancelled) return;
        setIsAdmin(false);
        setIsSuperAdmin(false);
      }

      // Resolve the approval status before clearing `loading` too, for the same
      // reason: a fresh signup must land on the application screen without the
      // dashboard rendering first. `loading` is cleared by the FIRST snapshot
      // (or the first error), not by subscribing.
      stopStatus = onSnapshot(
        doc(db, 'users', nextUser.uid),
        (snap) => {
          if (cancelled) return;
          setMustChangePassword(snap.exists() && snap.data().mustChangePassword === true);
          setMfaRequired(snap.exists() && snap.data().mfaRequired === true);
          setMfaEnrolled(snap.exists() && snap.data().mfaEnrolled === true);
          setAccountStatus(
            accountStatusFromDoc(
              snap.exists() ? (snap.data() as Record<string, unknown>) : undefined,
            ),
          );
          setLoading(false);
        },
        () => {
          // FAIL OPEN — deliberate, and the same rule the server applies. A
          // status that could not be READ proves nothing, so it must not be
          // grounds for shutting an approved customer out of the product. The
          // server gate is unaffected either way: it re-decides on every call.
          if (cancelled) return;
          setMustChangePassword(false);
          setMfaRequired(false);
          setMfaEnrolled(false);
          setAccountStatus('unreadable');
          setLoading(false);
        },
      );
    });

    return () => {
      cancelled = true;
      stopStatus?.();
      unsubscribe();
    };
  }, []);

  const login = async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password);
  };

  const signup = async (email: string, password: string) => {
    await createUserWithEmailAndPassword(auth, email, password);
  };

  const logout = async () => {
    if (!window.confirm('Are you sure you want to log out?')) return false;
    await signOut(auth);
    return true;
  };

  return (
    <AuthContext.Provider
      value={{ user, loading, isAdmin, isSuperAdmin, accountStatus, mustChangePassword, mfaRequired, mfaEnrolled, login, signup, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
